import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { MfaChallenge } from '../components/security/MfaChallenge';
import { toast } from 'sonner';
import { 
  Shield, Mail, Lock, User, ArrowRight, Loader2, Check,
  ChevronDown, Globe, Building2, Scale, Building, FileText,
  Crown, Sparkles, ShieldCheck
} from 'lucide-react';
import DataConsentModal from '@/components/DataConsentModal';
import { GoogleButton } from '../components/auth/GoogleButton';

// Profile configurations with plan associations
const PROFILES = [
  { 
    type: 'persona_natural', 
    name: 'Persona Natural', 
    icon: '👤',
    IconComponent: User,
    color: 'from-blue-500 to-blue-600',
    description: 'Ciudadano particular',
    plan: 'free',
    modules: 4
  },
  { 
    type: 'persona_juridica', 
    name: 'Persona Jurídica', 
    icon: '🏢',
    IconComponent: Building2,
    color: 'from-emerald-500 to-emerald-600',
    description: 'Empresas y organizaciones',
    plan: 'professional',
    modules: 6
  },
  { 
    type: 'abogado', 
    name: 'Abogado', 
    icon: '⚖️',
    IconComponent: Scale,
    color: 'from-purple-500 to-purple-600',
    description: 'Profesional del derecho',
    plan: 'professional',
    modules: 6
  },
  { 
    type: 'estudio_juridico', 
    name: 'Estudio Jurídico', 
    icon: '🏛️',
    IconComponent: Building,
    color: 'from-amber-500 to-amber-600',
    description: 'Firma de abogados',
    plan: 'enterprise',
    modules: 6
  },
  { 
    type: 'notario', 
    name: 'Notario', 
    icon: '📜',
    IconComponent: FileText,
    color: 'from-rose-500 to-rose-600',
    description: 'Ministro de fe pública',
    plan: 'enterprise',
    modules: 7
  },
  {
    type: 'administrador',
    name: 'Administrador',
    icon: '🛡️',
    IconComponent: ShieldCheck,
    color: 'from-amber-500 to-orange-600',
    description: 'Panel de administración (solo cuentas autorizadas)',
    plan: 'free',
    modules: 'todos los',
    adminOnly: true
  }
];

const COUNTRIES = [
  { code: 'chile', name: 'Chile', flag: '🇨🇱' },
  { code: 'ecuador', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'argentina', name: 'Argentina', flag: '🇦🇷' },
  { code: 'mexico', name: 'México', flag: '🇲🇽' },
  { code: 'colombia', name: 'Colombia', flag: '🇨🇴' },
  { code: 'peru', name: 'Perú', flag: '🇵🇪' },
  { code: 'brasil', name: 'Brasil', flag: '🇧🇷' },
  { code: 'espana', name: 'España', flag: '🇪🇸' },
  { code: 'eeuu', name: 'Estados Unidos', flag: '🇺🇸' }
];

const PLANS = {
  free: { name: 'Gratuito', color: 'text-gray-400', badge: null },
  professional: { name: 'Profesional', color: 'text-blue-400', badge: <Crown className="w-3 h-3" /> },
  enterprise: { name: 'Enterprise', color: 'text-amber-400', badge: <Sparkles className="w-3 h-3" /> }
};

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingRegisterData, setPendingRegisterData] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(PROFILES[0]);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });
  
  const { login, register, api, user, loading: checkingSession } = useAuth();
  const location = useLocation();
  const connectingGoogle = new URLSearchParams(location.search).get('connect_google') === '1';
  const navigate = useNavigate();
  useEffect(() => { if (user && !checkingSession && !loading && !mfaChallenge && !connectingGoogle) navigate('/dashboard', { replace: true }); }, [user, checkingSession, loading, mfaChallenge, connectingGoogle, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      setLoading(true);
      try {
        const res = await login(formData.email, formData.password);
        if (res?.mfa_required) { setMfaChallenge(res); return; }
        await afterLogin();
      } catch (error) {
        const message = error.response?.data?.detail;
        toast.error(typeof message === 'string' ? message : 'Error al procesar la solicitud');
      } finally {
        setLoading(false);
      }
    } else {
      if (selectedProfile.adminOnly) {
        toast.error('El perfil Administrador no se puede registrar: elige otro perfil o inicia sesión con una cuenta autorizada');
        return;
      }
      setPendingRegisterData({
        ...formData,
        profile_type: selectedProfile.type,
        country: selectedCountry.code
      });
      setShowConsentModal(true);
    }
  };

  const afterLogin = async () => {
        if (connectingGoogle) { navigate('/mi-perfil'); return; }
        if (selectedProfile.adminOnly) {
          let me = null;
          try { me = (await api.get('/auth/me')).data; } catch { /* handled below */ }
          if (me?.is_admin) {
            toast.success('Bienvenido al panel de administración 🛡️');
            navigate('/admin');
          } else {
            toast.error('Esta cuenta no tiene permisos de administrador. Entraste con tu perfil habitual.');
            navigate('/dashboard');
          }
          return;
        }
        // Save preferences after login
        try {
          await api.post('/platform/user/preferences/country', { country: selectedCountry.code });
          await api.post('/platform/user/preferences/profile', { profile_type: selectedProfile.type });
        } catch (prefError) {
          console.error('Error saving preferences:', prefError);
        }
        
        toast.success(`¡Bienvenido! ${selectedProfile.icon} ${selectedCountry.flag}`);
        navigate('/dashboard');
  };

  const handleConsentAccept = async (consentData) => {
    setShowConsentModal(false);
    setLoading(true);
    
    try {
      await register(
        pendingRegisterData.email, 
        pendingRegisterData.username, 
        pendingRegisterData.password,
        {
          ...consentData,
          profile_type: pendingRegisterData.profile_type,
          country: pendingRegisterData.country
        }
      );
      toast.success(`¡Cuenta creada! ${selectedProfile.icon}`);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear la cuenta';
      toast.error(message);
    } finally {
      setLoading(false);
      setPendingRegisterData(null);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const planInfo = PLANS[selectedProfile.plan];

  return (
    <div className="min-h-screen mesh-gradient flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6 animate-fade-in">
          <div className="flex items-center justify-center space-x-3 mb-3">
            <Shield className="w-10 h-10 text-blue-500" />
            <span className="text-2xl font-bold font-['Outfit']">NotaryOS</span>
          </div>
          <p className="text-slate-400 text-sm">
            La plataforma LegalTech #1 de Latinoamérica
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-xl animate-slide-up">
          {/* Toggle Login/Register */}
          <div className="flex bg-slate-900/50 rounded-lg p-1 mb-5">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              data-testid="login-tab-btn"
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                isLogin 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Iniciar Sesión
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              data-testid="register-tab-btn"
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                !isLogin 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Registrarse
            </button>
          </div>

          {/* Profile & Country Selection - BEFORE LOGIN */}
          {!mfaChallenge && <div className="mb-5 space-y-3">
            <GoogleButton disabled={loading} />
            {connectingGoogle && <p data-testid="google-explicit-link-notice" className="text-xs leading-6 text-amber-200">Este correo ya tiene cuenta. Ingresa con tu método actual y vincula Google desde Mi Perfil.</p>}
            <p className="text-center text-xs text-slate-500">o continúa con tu correo y contraseña</p>
          </div>}
          <div className="mb-5 space-y-3">
            {/* Profile Selector */}
            <div className="relative">
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
                Tipo de Usuario
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowProfileDropdown(!showProfileDropdown);
                  setShowCountryDropdown(false);
                }}
                data-testid="auth-profile-selector"
                className={`w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r ${selectedProfile.color} rounded-lg transition-all hover:opacity-90`}
              >
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-xl">{selectedProfile.icon}</span>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-semibold text-sm">{selectedProfile.name}</p>
                  <p className="text-white/70 text-xs">{selectedProfile.description} • {selectedProfile.modules} módulos</p>
                </div>
                <div className="flex items-center gap-2">
                  {planInfo.badge && (
                    <span className={`${planInfo.color} flex items-center gap-1 text-xs bg-black/20 px-2 py-1 rounded`}>
                      {planInfo.badge}
                      {planInfo.name}
                    </span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-white/70 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileDropdown(false)} />
                  <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-gray-700 bg-gray-900/50">
                      <p className="text-xs text-gray-400 uppercase tracking-wider px-2">Seleccionar Perfil</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {PROFILES.map((profile) => {
                        const pInfo = PLANS[profile.plan];
                        return (
                          <button
                            key={profile.type}
                            onClick={() => {
                              setSelectedProfile(profile);
                              setShowProfileDropdown(false);
                            }}
                            data-testid={`auth-profile-${profile.type}`}
                            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 transition-colors text-left ${
                              selectedProfile.type === profile.type ? 'bg-blue-900/30' : ''
                            }`}
                          >
                            <div className={`w-10 h-10 bg-gradient-to-r ${profile.color} rounded-lg flex items-center justify-center`}>
                              <span className="text-lg">{profile.icon}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium text-sm">{profile.name}</p>
                              <p className="text-gray-400 text-xs">{profile.description}</p>
                            </div>
                            <div className="text-right">
                              <span className={`${pInfo.color} text-xs flex items-center gap-1`}>
                                {pInfo.badge}
                                {pInfo.name}
                              </span>
                              <p className="text-gray-500 text-xs">{profile.modules} módulos</p>
                            </div>
                            {selectedProfile.type === profile.type && (
                              <Check className="w-5 h-5 text-blue-400" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Country Selector */}
            <div className="relative">
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
                País / Sistema Jurídico
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowCountryDropdown(!showCountryDropdown);
                  setShowProfileDropdown(false);
                }}
                data-testid="auth-country-selector"
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg border border-gray-700 transition-all"
              >
                <Globe className="w-5 h-5 text-blue-400" />
                <span className="text-2xl">{selectedCountry.flag}</span>
                <span className="flex-1 text-left text-white font-medium">{selectedCountry.name}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Country Dropdown */}
              {showCountryDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowCountryDropdown(false)} />
                  <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                    {COUNTRIES.map((country) => (
                      <button
                        key={country.code}
                        onClick={() => {
                          setSelectedCountry(country);
                          setShowCountryDropdown(false);
                        }}
                        data-testid={`auth-country-${country.code}`}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 transition-colors text-left ${
                          selectedCountry.code === country.code ? 'bg-blue-900/30' : ''
                        }`}
                      >
                        <span className="text-2xl">{country.flag}</span>
                        <span className="flex-1 text-white">{country.name}</span>
                        {selectedCountry.code === country.code && (
                          <Check className="w-5 h-5 text-blue-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-card text-gray-500">Credenciales de acceso</span>
            </div>
          </div>

          {/* Form */}
          {mfaChallenge ? (
            <MfaChallenge challenge={mfaChallenge} onDone={afterLogin} onCancel={() => setMfaChallenge(null)} />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  placeholder="tu@email.com"
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-white placeholder-slate-500 text-sm"
                />
              </div>
            </div>

            {!isLogin && (
              <div className="animate-fade-in">
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Nombre de usuario
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required={!isLogin}
                    data-testid="username-input"
                    placeholder="Tu nombre"
                    className="w-full pl-11 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-white placeholder-slate-500 text-sm"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  placeholder="••••••••"
                  minLength={6}
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-white placeholder-slate-500 text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full btn-primary py-2.5 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>{isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
          )}

          {isLogin && !mfaChallenge && (
            <p className="mt-3 text-center text-sm">
              <a href="/recuperar-clave" data-testid="forgot-password-link" className="text-blue-400 hover:underline">¿Olvidaste tu contraseña?</a>
            </p>
          )}


          {/* Legal Links */}
          <div className="mt-4 pt-3 border-t border-slate-700">
            <p className="text-center text-xs text-slate-500">
              Al {isLogin ? 'iniciar sesión' : 'registrarte'}, aceptas nuestros{' '}
              <Link to="/legal/terminos" className="text-blue-400 hover:underline">
                Términos
              </Link>{' '}
              y{' '}
              <Link to="/legal/privacidad" className="text-blue-400 hover:underline">
                Política de Privacidad
              </Link>
            </p>
          </div>
        </div>

        {/* Plan upgrade hint */}
        {selectedProfile.plan !== 'free' && (
          <div className="mt-4 text-center animate-fade-in">
            <p className="text-xs text-slate-400">
              El perfil <strong className="text-white">{selectedProfile.name}</strong> requiere plan{' '}
              <span className={planInfo.color}>{planInfo.name}</span>
            </p>
            <Link to="/planes" className="text-xs text-blue-400 hover:underline">
              Ver planes y precios →
            </Link>
          </div>
        )}
      </div>

      {/* Consent Modal */}
      <DataConsentModal
        isOpen={showConsentModal}
        onClose={() => {
          setShowConsentModal(false);
          setPendingRegisterData(null);
        }}
        onAccept={handleConsentAccept}
      />
    </div>
  );
};

export default AuthPage;
